import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Braces, Plus } from 'lucide-react';
import IntentTreeNode from '../../../../types/BotBuilder/IntentTreeNode';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import RichTextInput from './RichTextInput/RichTextInput';
import IntentSlot from '../../../../types/BotBuilder/IntentSlot';
import insertChipAtCaret from './slotInserter';
import SampleUtteranceItem from './SampleUtteranceItem';
import { cn } from '../../../../lib/utils';

interface IntentNodeSettingsProps {
  node: BotResponseTreeNode;
  onNodeUpdate: (node: BotResponseTreeNode) => void;
  isLocked?: boolean;
}

const EMPTY_SLOTS_MESSAGE =
  'No variables found. Please add a collect-variable block in the editor.';

function IntentNodeSettings({
  node,
  onNodeUpdate,
  isLocked = false
}: IntentNodeSettingsProps): React.ReactElement {
  const { intent } = useContext(BotBuilderContext);

  const [sampleUtterances, setSampleUtterances] = useState<string[]>([]);
  const [newInputValue, setNewInputValue] = useState('');
  const [addExpanded, setAddExpanded] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedUtterance, setEditedUtterance] = useState('');
  const [slotMenuAnchor, setSlotMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [clickedChipEl, setClickedChipEl] = useState<HTMLSpanElement | null>(
    null
  );
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );

  const utterancesRef = useRef<HTMLDivElement>(null);
  const newInputRef = useRef<HTMLDivElement>(null);
  const prevCount = useRef(0);
  const savedRangeRef = useRef<Range | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const intentNode = node.content as IntentTreeNode;
    setSampleUtterances(intentNode.sampleUtterances);
  }, [node]);

  useEffect(() => {
    if (utterancesRef.current && sampleUtterances.length > prevCount.current) {
      utterancesRef.current.scrollTo({
        top: utterancesRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
    prevCount.current = sampleUtterances.length;
  }, [sampleUtterances]);

  useEffect(() => {
    if (!addExpanded || isLocked) return;
    newInputRef.current?.focus();
  }, [addExpanded, isLocked, sampleUtterances.length]);

  const finishEditingIfAny = (): void => {
    if (editingIndex === null) return;
    const txt = editedUtterance.trim();
    if (txt) {
      const copy = [...sampleUtterances];
      copy[editingIndex] = txt;
      const intentNode = node.content as IntentTreeNode;
      intentNode.sampleUtterances = copy;
      setSampleUtterances(copy);
      onNodeUpdate(node);
    }
    setEditingIndex(null);
  };

  const expandAddForm = (): void => {
    finishEditingIfAny();
    setAddExpanded(true);
  };

  const handleAddBlur = (): void => {
    if (!newInputValue.trim()) {
      setAddExpanded(false);
      setNewInputValue('');
      if (newInputRef.current) newInputRef.current.innerHTML = '';
    }
  };

  useLayoutEffect(() => {
    if (!slotMenuAnchor) {
      setMenuPos(null);
      return;
    }
    const rect = slotMenuAnchor.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left });
  }, [slotMenuAnchor]);

  useEffect(() => {
    if (!slotMenuAnchor) return undefined;
    const handler = (e: MouseEvent): void => {
      if (menuRef.current?.contains(e.target as Node)) return;
      if (slotMenuAnchor.contains(e.target as Node)) return;
      setSlotMenuAnchor(null);
      setClickedChipEl(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [slotMenuAnchor]);

  const saveUtterances = (utterances: string[]): void => {
    setSampleUtterances(utterances);
    const intentNode = node.content as IntentTreeNode;
    intentNode.sampleUtterances = utterances;
    onNodeUpdate(node);
  };

  const handleCreateUtterance = (): void => {
    const txt = newInputValue.trim();
    if (!txt) return;
    saveUtterances([...sampleUtterances, txt]);
    setNewInputValue('');
    if (newInputRef.current) newInputRef.current.innerHTML = '';
  };

  const handleDeleteUtterance = (idx: number): void =>
    saveUtterances(sampleUtterances.filter((_, i) => i !== idx));

  const finishEditing = (idx: number): void => {
    const txt = editedUtterance.trim();
    if (txt) {
      const copy = [...sampleUtterances];
      copy[idx] = txt;
      saveUtterances(copy);
    }
    setEditingIndex(null);
  };

  const openSlotMenu = (anchor: HTMLElement): void => setSlotMenuAnchor(anchor);
  const closeSlotMenu = (): void => {
    setSlotMenuAnchor(null);
    setClickedChipEl(null);
  };

  const handleBracesMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    setClickedChipEl(null);

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }

    openSlotMenu(e.currentTarget);
  };

  const restoreSavedCaret = (): void => {
    if (savedRangeRef.current) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedRangeRef.current);
      savedRangeRef.current = null;
    }
  };

  const insertSlot = (slot: IntentSlot): void => {
    const token = `{${slot.slotName}}`;

    if (clickedChipEl && editingIndex !== null) {
      clickedChipEl.textContent = token;
      const slotName = token.slice(1, -1);
      clickedChipEl.setAttribute('data-slot-name', slotName);
      const editingEl = document.querySelector(
        '[data-editing-utterance="true"]'
      ) as HTMLDivElement;
      if (editingEl) {
        setEditedUtterance(editingEl.innerText);
        editingEl.focus();
      }
      closeSlotMenu();
      return;
    }

    if (editingIndex !== null) {
      restoreSavedCaret();
      const editingEl = document.querySelector(
        '[data-editing-utterance="true"]'
      ) as HTMLDivElement;
      if (editingEl) {
        insertChipAtCaret(editingEl, token);
        setEditedUtterance(editingEl.innerText);
      }
      closeSlotMenu();
      return;
    }

    if (newInputRef.current) {
      restoreSavedCaret();
      insertChipAtCaret(newInputRef.current, token);
      setNewInputValue(newInputRef.current.innerText);
      closeSlotMenu();
      return;
    }

    setNewInputValue((prev) =>
      prev === '' || prev.endsWith(' ') ? `${prev}${token}` : `${prev} ${token}`
    );
    closeSlotMenu();
  };

  const slotAdornment = (
    <button
      type='button'
      tabIndex={-1}
      onMouseDown={handleBracesMouseDown}
      aria-label='Insert variable'
      className='ml-2 flex shrink-0 items-center justify-center rounded p-1 text-white/50 hover:text-white'
    >
      <Braces size={16} />
    </button>
  );

  return (
    <div className='flex flex-col text-white'>
      <div ref={utterancesRef} className='flex flex-col gap-1.5 w-full'>
        {sampleUtterances.map((utterance, idx) => (
          <SampleUtteranceItem
            key={idx}
            utterance={utterance}
            index={idx}
            isEditing={editingIndex === idx}
            editedValue={editedUtterance}
            onEditStart={(index, utt) => {
              setEditingIndex(index);
              setEditedUtterance(utt);
            }}
            onEditChange={setEditedUtterance}
            onEditFinish={finishEditing}
            onDelete={handleDeleteUtterance}
            onSlotClick={(chip) => {
              setClickedChipEl(chip);
              openSlotMenu(chip);
            }}
            onBracesMouseDown={handleBracesMouseDown}
            isLocked={isLocked}
          />
        ))}
      </div>

      {!isLocked && addExpanded && (
        <div className='mt-2 flex w-full items-stretch gap-2'>
          <div className='flex flex-1 items-center rounded-md border border-white/20 bg-transparent px-1 focus-within:border-secondary'>
            <RichTextInput
              ref={newInputRef}
              value={newInputValue}
              onChange={setNewInputValue}
              placeholder='Add a trigger phrase…'
              fullWidth
              transparent
              whiteText
              onBlur={handleAddBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateUtterance();
                }
              }}
              onSlotClick={(chip) => {
                setClickedChipEl(chip);
                openSlotMenu(chip);
              }}
              InputProps={{ endAdornment: slotAdornment }}
            />
          </div>
          <button
            type='button'
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleCreateUtterance}
            disabled={!newInputValue.trim()}
            className='shrink-0 rounded-md bg-secondary px-4 text-sm font-medium text-white hover:bg-secondary/90 disabled:opacity-50'
          >
            Add
          </button>
        </div>
      )}

      {!isLocked && !addExpanded && (
        <div className='mt-2 flex w-full justify-end'>
          <button
            type='button'
            onClick={expandAddForm}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-md',
              'border border-white/20 bg-transparent px-3 text-sm',
              'text-white/80 hover:bg-white/5 hover:text-white'
            )}
          >
            <Plus size={14} />
            Add trigger
          </button>
        </div>
      )}

      {slotMenuAnchor &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            role='menu'
            className='fixed z-50 min-w-[10rem] overflow-hidden rounded-md border border-border bg-popover py-1 text-popover-foreground shadow-md'
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {intent?.slots.length ? (
              intent.slots.map((s) => (
                <button
                  key={s.id ?? s.slotName}
                  type='button'
                  role='menuitem'
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertSlot(s)}
                  className='block w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground'
                >
                  {s.slotName}
                </button>
              ))
            ) : (
              <div className='px-3 py-1.5 text-sm text-muted-foreground'>
                {EMPTY_SLOTS_MESSAGE}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default IntentNodeSettings;
